'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  className?: string;
  inline?: boolean;
}

export default function Tooltip({
  content,
  children,
  position = 'top',
  delay = 100,
  className = '',
  inline = false
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isPositioned, setIsPositioned] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const mousePos = useRef({ x: 0, y: 0 }); // Use ref to avoid stale state in timeouts
  const [isMounted, setIsMounted] = useState(false);
  const childRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const showTooltip = (e: React.MouseEvent) => {
    // Capture initial mouse position
    mousePos.current = { x: e.clientX, y: e.clientY };

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setIsVisible(true);
      // We don't need another timeout here, just update immediately or let effect handle it
      // But keeping the requestAnimationFrame or timeout ensures render has happened
      requestAnimationFrame(updatePosition);
    }, delay);
  };

  const hideTooltip = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsVisible(false);
    setIsPositioned(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    mousePos.current = { x: e.clientX, y: e.clientY };
    if (isVisible) {
      updatePosition();
    }
  };

  const updatePosition = useCallback(() => {
    if (!tooltipRef.current) return;

    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const padding = 10;
    const offset = 15; // Distance from cursor

    let top = mousePos.current.y + offset;
    let left = mousePos.current.x + offset;

    // Check if tooltip would go off screen on the right
    if (left + tooltipRect.width > window.innerWidth - padding) {
      // Position to the left of cursor instead
      left = mousePos.current.x - tooltipRect.width - offset;
    }

    // Check if tooltip would go off screen on the bottom
    if (top + tooltipRect.height > window.innerHeight - padding) {
      // Position above cursor instead
      top = mousePos.current.y - tooltipRect.height - offset;
    }

    // Ensure tooltip stays within viewport bounds
    top = Math.max(padding, Math.min(top, window.innerHeight - tooltipRect.height - padding));
    left = Math.max(padding, Math.min(left, window.innerWidth - tooltipRect.width - padding));

    setTooltipStyle({
      top: `${top}px`,
      left: `${left}px`
    });

    // Mark as positioned to show tooltip
    setIsPositioned(true);
  }, []); // No dependency on mousePos as it's a ref

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // Update position when tooltip becomes visible
  useEffect(() => {
    if (isVisible) {
      updatePosition();
    }
  }, [isVisible, updatePosition]);

  // Force update position when content changes
  useEffect(() => {
    if (isVisible) {
      updatePosition();
    }
  }, [content, isVisible, updatePosition]);

  const tooltipElement = isVisible && isMounted ? (
    <div
      ref={tooltipRef}
      className="fixed z-[999] max-w-xs px-3 py-2 text-xs text-white bg-gray-800 rounded shadow-lg whitespace-pre-wrap break-words pointer-events-none transition-opacity duration-100"
      style={{
        ...tooltipStyle,
        opacity: isPositioned ? 1 : 0
      }}
    >
      {content}
    </div>
  ) : null;

  return (
    <>
      <div
        className={`relative ${inline ? 'inline-block w-auto' : 'block w-full'} ${className}`}
        ref={childRef}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onMouseMove={handleMouseMove}
      >
        {children}
      </div>
      {isMounted && tooltipElement && createPortal(tooltipElement, document.body)}
    </>
  );
}
